import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { aigleInputStyles } from '@styles/inputStyleGuide';
import { aigleColor } from '@styles/colorStyleGuide';

/**
 * AigleInput Props
 */
export interface AigleInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input 상태 */
  state?: 'default' | 'active' | 'error' | 'disabled';
  /** Label 텍스트 */
  label?: string;
  /** Helper/Error 메시지 */
  helperText?: string;
  /** 에러 상태 여부 */
  error?: boolean;
  /** 최대 글자수 (카운터 표시) */
  maxLength?: number;
  /** 현재 글자수 표시 여부 */
  showCounter?: boolean;
  /** 시작 아이콘/요소 */
  startAdornment?: React.ReactNode;
  /** 끝 아이콘/요소 */
  endAdornment?: React.ReactNode;
  /** 전체 너비 사용 여부 */
  fullWidth?: boolean;
  /** Floating Label 사용 여부 (MUI 스타일) */
  floatingLabel?: boolean;
  /** 추가 스타일 */
  customStyle?: SerializedStyles;
  /** 컨테이너 추가 스타일 */
  containerStyle?: SerializedStyles;
}

// Styled Components
const InputContainer = styled.div<{ $fullWidth: boolean; $containerStyle?: SerializedStyles }>`
  ${aigleInputStyles.container}
  ${({ $fullWidth }) => $fullWidth ? 'width: 100%;' : 'width: auto;'}
  ${({ $containerStyle }) => $containerStyle}

`;

const InputLabel = styled.label`
  ${aigleInputStyles.label}
`;

const FloatingLabel = styled.label<{ $active: boolean; $error: boolean }>`
  ${aigleInputStyles.floatingLabel}
  ${({ $active }) => $active && css`
    top: 0;
    font-size: 12px;
    color: ${aigleColor.brand.primary};
  `}
  ${({ $error, $active }) => $error && css`
    color: ${$active ? aigleColor.semantic.error : aigleColor.semantic.error};
  `}
`;

const InputWrapper = styled.div<{ $hasAdornment: boolean; $floatingLabel: boolean }>`
  ${aigleInputStyles.wrapper}
  position: relative;
  ${({ $floatingLabel }) => $floatingLabel && css`
    margin-top: 8px;
  `}
`;

const StyledInput = styled.input<{
  $state: 'default' | 'active' | 'error' | 'disabled';
  $hasStartAdornment: boolean;
  $hasEndAdornment: boolean;
  $customStyle?: SerializedStyles;
}>`
  ${({ $state }) => aigleInputStyles.state[$state]}
  ${({ $hasStartAdornment, $hasEndAdornment }) => {
    if ($hasStartAdornment && $hasEndAdornment) return aigleInputStyles.withAdornment.both;
    if ($hasStartAdornment) return aigleInputStyles.withAdornment.start;
    if ($hasEndAdornment) return aigleInputStyles.withAdornment.end;
    return '';
  }}
  ${({ $customStyle }) => $customStyle}
  border-radius: 8px;
`;

const StartAdornment = styled.div`
  ${aigleInputStyles.adornment.start}
`;

const EndAdornment = styled.div`
  ${aigleInputStyles.adornment.end}
`;

const HelperTextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const HelperText = styled.span<{ $error: boolean }>`
  ${aigleInputStyles.helperText.base}
  ${({ $error }) => $error ? aigleInputStyles.helperText.error : aigleInputStyles.helperText.default}
`;

const CharacterCounter = styled.span<{ $error: boolean }>`
  ${aigleInputStyles.counter}
  ${({ $error }) => $error && css`color: ${aigleInputStyles.helperText.error};`}
`;

/**
 * AigleInput Component
 * 
 * 일관된 디자인의 Input 컴포넌트
 * 
 * @example
 * // Basic Input
 * <AigleInput placeholder="입력하세요" />
 * 
 * @example
 * // Input with Label
 * <AigleInput label="이름" placeholder="이름을 입력하세요" />
 * 
 * @example
 * // Error State with Message
 * <AigleInput 
 *   label="이메일" 
 *   error 
 *   helperText="올바른 이메일을 입력하세요"
 *   placeholder="email@example.com"
 * />
 * 
 * @example
 * // With Character Counter
 * <AigleInput 
 *   label="제목"
 *   maxLength={50}
 *   showCounter
 *   placeholder="제목을 입력하세요"
 * />
 * 
 * @example
 * // Floating Label (MUI Style)
 * <AigleInput 
 *   label="이메일"
 *   floatingLabel
 *   placeholder="email@example.com"
 * />
 */
export const AigleInput = React.forwardRef<HTMLInputElement, AigleInputProps>(
  (
    {
      state,
      label,
      helperText,
      error = false,
      maxLength,
      showCounter = false,
      startAdornment,
      endAdornment,
      fullWidth = true,
      floatingLabel = false,
      customStyle,
      containerStyle,
      disabled = false,
      value,
      placeholder,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    
    // 상태 결정
    const effectiveState = disabled ? 'disabled' : error ? 'error' : state || 'default';
    
    // 현재 글자수 계산
    const currentLength = value ? String(value).length : 0;
    
    // Floating label 활성화 여부
    const isFloatingLabelActive = floatingLabel && (isFocused || (value && String(value).length > 0));
    
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };
    
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };
    
    return (
      <InputContainer $fullWidth={fullWidth} $containerStyle={containerStyle}>
        {/* 일반 Label (floatingLabel이 false일 때) */}
        {label && !floatingLabel && <InputLabel>{label}</InputLabel>}
        
        <InputWrapper $hasAdornment={!!(startAdornment || endAdornment)} $floatingLabel={floatingLabel}>
          {/* Floating Label (floatingLabel이 true일 때) */}
          {label && floatingLabel && (
            <FloatingLabel 
              $active={isFloatingLabelActive} 
              $error={error}
              className={isFloatingLabelActive ? 'active' : ''}
            >
              {label}
            </FloatingLabel>
          )}
          
          {startAdornment && <StartAdornment>{startAdornment}</StartAdornment>}
          
          <StyledInput
            ref={ref}
            $state={effectiveState}
            $hasStartAdornment={!!startAdornment}
            $hasEndAdornment={!!endAdornment || showCounter}
            $customStyle={customStyle}
            disabled={disabled}
            maxLength={maxLength}
            value={value}
            placeholder={floatingLabel ? undefined : placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          
          {endAdornment && <EndAdornment>{endAdornment}</EndAdornment>}
        </InputWrapper>
        
        {(helperText || showCounter) && (
          <HelperTextWrapper>
            {helperText && (
              <HelperText $error={error}>{helperText}</HelperText>
            )}
            {showCounter && maxLength && (
              <CharacterCounter $error={error && currentLength >= maxLength}>
                {currentLength}/{maxLength}
              </CharacterCounter>
            )}
          </HelperTextWrapper>
        )}
      </InputContainer>
    );
  }
);

AigleInput.displayName = 'AigleInput';

export default AigleInput;

